import {useMemo} from 'react';
import {FieldMetadata} from '@/interfaces/form';
import {AdvisorRole, AdvisorType} from '@/repositories/beneva-api/endpoints/users';

interface FieldsMetadata {
  language: FieldMetadata;
  cons1_lastName: FieldMetadata;
  cons2_firstName: FieldMetadata;
  cons4_email: FieldMetadata;
  cons5_email_confirmation: FieldMetadata;
  cons6_phoneNumber: FieldMetadata;
  cons7_phoneExtension: FieldMetadata;
  cons8_generalAgentName: FieldMetadata;
  cons9_generalAgentCode: FieldMetadata;
  cons25_advisorCode: FieldMetadata;
  altAdvisorCode1: FieldMetadata;
  altAdvisorCode2: FieldMetadata;
  altAdvisorCode3: FieldMetadata;
  altAdvisorCode4: FieldMetadata;
  cons16_isFundServe: FieldMetadata;
  cons10_generalAgentEmail: FieldMetadata;
  cons11_generalAgentEmail_confirmation: FieldMetadata;
}

interface UseFieldsMetadataProps {
  advisorRole?: AdvisorRole;
  advisorType?: AdvisorType;
}

export const useAdvisorFormFieldsMetadata = ({advisorRole, advisorType}: UseFieldsMetadataProps): FieldsMetadata => {
  return useMemo<FieldsMetadata>(() => {
    const isEmployee = advisorRole === AdvisorRole.Employee;
    const isAssistant = advisorRole === AdvisorRole.Assistant;
    const isBenevaFinancialAdvisor = advisorType === AdvisorType.BenevaFinancialAdvisor;

    const isEmployeeOrAssistant = isEmployee || isAssistant;

    // Code fields related flags
    const requiresCodeFields = !isAssistant;
    const codeFieldsHidden = !requiresCodeFields;

    // General agent fields related flags
    const requiresGeneralAgentFields = !(isBenevaFinancialAdvisor || isAssistant);
    const generalAgentFieldsHidden = !requiresGeneralAgentFields;

    return {
      language: {
        isReadOnly: false,
        isRequired: true,
        isHidden: false,
      },
      cons1_lastName: {
        isReadOnly: !isEmployeeOrAssistant,
        isRequired: true,
        isHidden: false,
      },
      cons2_firstName: {
        isReadOnly: !isEmployeeOrAssistant,
        isRequired: true,
        isHidden: false,
      },
      cons4_email: {
        isReadOnly: false,
        isRequired: true,
        isHidden: false,
      },
      cons5_email_confirmation: {
        isReadOnly: false,
        isRequired: true,
        isHidden: false,
      },
      cons6_phoneNumber: {
        isReadOnly: false,
        isRequired: true,
        isHidden: false,
      },
      cons7_phoneExtension: {
        isReadOnly: false,
        isRequired: false,
        isHidden: false,
      },
      cons8_generalAgentName: {
        isReadOnly: false,
        isRequired: requiresGeneralAgentFields,
        isHidden: generalAgentFieldsHidden,
      },
      cons9_generalAgentCode: {
        isReadOnly: !isEmployee,
        isRequired: requiresCodeFields,
        isHidden: codeFieldsHidden,
      },
      cons25_advisorCode: {
        isReadOnly: !isEmployee,
        isRequired: requiresCodeFields,
        isHidden: codeFieldsHidden,
      },
      altAdvisorCode1: {
        isReadOnly: !isEmployee,
        isRequired: false,
        isHidden: codeFieldsHidden,
      },
      altAdvisorCode2: {
        isReadOnly: !isEmployee,
        isRequired: false,
        isHidden: codeFieldsHidden,
      },
      altAdvisorCode3: {
        isReadOnly: !isEmployee,
        isRequired: false,
        isHidden: codeFieldsHidden,
      },
      altAdvisorCode4: {
        isReadOnly: !isEmployee,
        isRequired: false,
        isHidden: codeFieldsHidden,
      },
      cons16_isFundServe: {
        isReadOnly: false,
        isRequired: true,
        isHidden: isBenevaFinancialAdvisor || isAssistant,
      },
      cons10_generalAgentEmail: {
        isReadOnly: false,
        isRequired: requiresGeneralAgentFields,
        isHidden: generalAgentFieldsHidden,
      },
      cons11_generalAgentEmail_confirmation: {
        isReadOnly: false,
        isRequired: requiresGeneralAgentFields,
        isHidden: generalAgentFieldsHidden,
      },
    } satisfies FieldsMetadata;
  }, [advisorRole, advisorType]);
};
